import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdCreditCard, MdEdit, MdSave, MdBlock, MdCheckCircle, MdRefresh, MdWarning, MdShield } from 'react-icons/md';

function AdminSubscriptions() {
  const [activeTab, setActiveTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlans, setEditingPlans] = useState(false);
  const [editedPlans, setEditedPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [initLoading, setInitLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [plansRes, subsRes, exRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/subscription-plans`),
        axios.get(`${API_URL}/api/admin/subscriptions`),
        axios.get(`${API_URL}/api/admin/subscriptions/exemptions`),
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setExemptions(exRes.data.exemptions || []);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEditPlans = () => {
    setEditedPlans(JSON.parse(JSON.stringify(plans)));
    setEditingPlans(true);
  };

  const savePlans = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${API_URL}/api/admin/subscription-plans`, { plans: editedPlans });
      setPlans(editedPlans);
      setEditingPlans(false);
      setMessage('Plans updated successfully');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to update plans');
    } finally {
      setSaving(false);
    }
  };

  const updatePlanField = (idx, field, value) => {
    const updated = [...editedPlans];
    updated[idx] = { ...updated[idx], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    setEditedPlans(updated);
  };

  const toggleExemption = async (userId, currentlyExempt) => {
    try {
      await axios.put(`${API_URL}/api/admin/subscriptions/${userId}/exempt`, { exempt: !currentlyExempt });
      setMessage(`Exemption ${!currentlyExempt ? 'granted' : 'revoked'}`);
      fetchAll();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to update exemption');
    }
  };

  const revokeProfile = async (userId, displayName) => {
    if (!window.confirm(`Are you sure you want to revoke the representative profile for ${displayName || 'this user'}? This will revert them to a voter account and cancel their billing.`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/subscriptions/${userId}/revoke`);
      setMessage(res.data.message || 'Representative profile revoked successfully.');
      fetchAll();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to revoke profile');
    }
  };

  const initExemptions = async () => {
    if (!window.confirm('WARNING: This will mark ALL current active representative profiles as billing-exempt. This is a bulk operation. Are you absolutely sure?')) return;
    if (!window.confirm('SECOND CONFIRMATION: This cannot be easily undone. Only proceed if you intend to exempt ALL active representatives from billing.')) return;
    setInitLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/subscriptions/init-exemptions`);
      setMessage(res.data.message);
      fetchAll();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to initialize exemptions');
    } finally {
      setInitLoading(false);
    }
  };

  const cleanupExemptions = async () => {
    const protectedUsers = exemptions.filter(e => e.username === 'user1234').map(e => e.user_id);
    const removeCount = exemptions.length - protectedUsers.length;
    if (removeCount === 0) {
      setMessage('No exemptions to clean up.');
      return;
    }
    if (!window.confirm(`This will remove ${removeCount} exemption(s), keeping only protected accounts (user1234 / Thomas Miller). Continue?`)) return;
    setCleanupLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/subscriptions/cleanup-exemptions`, { keep_user_ids: protectedUsers });
      setMessage(res.data.message);
      fetchAll();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to cleanup exemptions');
    } finally {
      setCleanupLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      cancelling: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-600',
      exempt: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
        {status === 'active' && <MdCheckCircle className="text-sm" />}
        {status === 'exempt' && <MdShield className="text-sm" />}
        {status === 'cancelling' && <MdWarning className="text-sm" />}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 pt-8 py-6" data-testid="admin-subscriptions-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <MdCreditCard className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik">Subscription Management</h1>
            <p className="text-sm text-gray-500 font-rubik">Manage representative subscription plans & billing</p>
          </div>
        </div>
        <button onClick={fetchAll} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors" data-testid="refresh-subscriptions-btn">
          <MdRefresh className="text-lg" /> Refresh
        </button>
      </div>

      {message && (
        <div className={`rounded-lg p-3 mb-4 text-sm font-rubik ${message.includes('Failed') || message.includes('error') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`} data-testid="admin-sub-message">
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6" data-testid="admin-sub-tabs">
        {[
          { id: 'plans', label: 'Pricing Plans' },
          { id: 'subscriptions', label: `All Subscriptions (${subscriptions.length})` },
          { id: 'exemptions', label: `Exemptions (${exemptions.length})` },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === tab.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`admin-sub-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 font-rubik">Subscription Plans</h2>
            {!editingPlans ? (
              <button onClick={startEditPlans} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-rubik" data-testid="edit-plans-btn">
                <MdEdit className="text-lg" /> Edit Pricing
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setEditingPlans(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-rubik">Cancel</button>
                <button onClick={savePlans} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-rubik disabled:opacity-50" data-testid="save-plans-btn">
                  <MdSave className="text-lg" /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(editingPlans ? editedPlans : plans).map((plan, idx) => (
              <div key={plan.id} className="border rounded-lg p-5" data-testid={`plan-card-${plan.id}`}>
                <h3 className="text-lg font-semibold text-gray-800 font-rubik mb-1">{plan.name}</h3>
                <p className="text-xs text-gray-500 font-rubik mb-3">{plan.description}</p>
                {editingPlans ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 font-rubik">Amount ($)</label>
                      <input type="number" step="0.01" value={editedPlans[idx]?.amount || ''} onChange={(e) => updatePlanField(idx, 'amount', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid={`plan-amount-input-${plan.id}`} />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-rubik">Description</label>
                      <input type="text" value={editedPlans[idx]?.description || ''} onChange={(e) => updatePlanField(idx, 'description', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-primary font-rubik">${plan.amount.toFixed(2)}<span className="text-sm font-normal text-gray-500">/{plan.interval_months === 1 ? 'mo' : plan.interval_months === 3 ? 'qtr' : 'yr'}</span></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 font-rubik">All Subscriptions</h2>
          </div>
          {subscriptions.length === 0 ? (
            <div className="p-8 text-center text-gray-500 font-rubik">No subscriptions yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subscriptions-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">User</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Office & Location</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Plan</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Period End</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600 font-rubik">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {subscriptions.map((sub) => (
                    <tr key={sub.user_id} className="hover:bg-gray-50" data-testid={`sub-row-${sub.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {sub.profile_picture_url ? (
                            <img
                              src={sub.profile_picture_url}
                              alt={sub.display_name || sub.username}
                              className="w-9 h-9 rounded-full object-cover border border-gray-200 flex-shrink-0"
                              data-testid={`sub-avatar-${sub.user_id}`}
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" data-testid={`sub-avatar-${sub.user_id}`}>
                              <MdCreditCard className="text-gray-400 text-sm" />
                            </div>
                          )}
                          <div className="min-w-0">
                            {sub.display_name && (
                              <p className="font-medium text-gray-800 font-rubik text-sm truncate" data-testid={`sub-display-name-${sub.user_id}`}>
                                {sub.display_name}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 font-rubik truncate">@{sub.username || sub.user_id?.slice(0, 8)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          {sub.office && (
                            <p className="text-sm text-gray-800 font-rubik font-medium truncate" data-testid={`sub-office-${sub.user_id}`}>{sub.office}</p>
                          )}
                          <p className="text-xs text-gray-500 font-rubik truncate">
                            {[sub.running_city_county, sub.running_state].filter(Boolean).join(', ') || 'No location'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-rubik">{sub.plan_name}</td>
                      <td className="px-4 py-3">{getStatusBadge(sub.status)}</td>
                      <td className="px-4 py-3 font-rubik">{sub.amount > 0 ? `$${sub.amount.toFixed(2)}` : 'Free'}</td>
                      <td className="px-4 py-3 font-rubik text-gray-500">{formatDate(sub.current_period_end)}</td>
                      <td className="px-4 py-3 text-right">
                        {sub.status !== 'cancelled' && (
                          <button
                            onClick={() => revokeProfile(sub.user_id, sub.display_name || sub.username)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg text-xs font-medium font-rubik transition-colors"
                            data-testid={`revoke-btn-${sub.user_id}`}
                          >
                            <MdBlock className="text-sm" /> Revoke
                          </button>
                        )}
                        {sub.status === 'cancelled' && (
                          <span className="text-xs text-gray-400 font-rubik">Revoked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Exemptions Tab */}
      {activeTab === 'exemptions' && (
        <div>
          <div className="bg-white rounded-xl shadow-sm border mb-4">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800 font-rubik">Billing Exemptions</h2>
              <div className="flex gap-2">
                {exemptions.length > 1 && (
                  <button
                    onClick={cleanupExemptions}
                    disabled={cleanupLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-rubik disabled:opacity-50"
                    data-testid="cleanup-exemptions-btn"
                  >
                    <MdBlock className="text-lg" /> {cleanupLoading ? 'Cleaning...' : 'Cleanup Exemptions'}
                  </button>
                )}
                <button
                  onClick={initExemptions}
                  disabled={initLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-rubik disabled:opacity-50"
                  data-testid="init-exemptions-btn"
                >
                  <MdShield className="text-lg" /> {initLoading ? 'Processing...' : 'Initialize Exemptions'}
                </button>
              </div>
            </div>
            <p className="px-4 py-2 text-xs text-gray-500 font-rubik border-b bg-gray-50">
              Billing exemptions are only for accounts that should never be charged (e.g., the primary representative account). Use "Cleanup Exemptions" to remove all exemptions except the protected account (user1234 / Thomas Miller).
            </p>
            {exemptions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 font-rubik">No exemptions configured</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="exemptions-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Username</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Representative</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Office</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Status</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600 font-rubik">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {exemptions.map((ex) => (
                      <tr key={ex.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-rubik">{ex.username}</td>
                        <td className="px-4 py-3 font-rubik">{ex.rep_name}</td>
                        <td className="px-4 py-3 font-rubik text-gray-500">{ex.office}</td>
                        <td className="px-4 py-3">{getStatusBadge(ex.status)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => toggleExemption(ex.user_id, ex.billing_exempt)}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium font-rubik transition-colors ${
                              ex.billing_exempt
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                            }`}
                            data-testid={`toggle-exempt-btn-${ex.user_id}`}
                          >
                            {ex.billing_exempt ? 'Remove Exemption' : 'Grant Exemption'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSubscriptions;
